(() => {
  "use strict";

  const root = document.querySelector("[data-customizer-root]");
  if (!root) return;

  const apiBase = document.body.dataset.customizationApi.replace(/\/+$/, "");
  const isLocalDemo = ["127.0.0.1", "localhost"].includes(window.location.hostname);
  const supportedLocales = new Set(["en-US", "de-DE", "it-IT", "es-ES", "fr-FR"]);
  const requestedLocale = localStorage.getItem("formOfUsLocale") || document.documentElement.lang || "en-US";
  const locale = supportedLocales.has(requestedLocale) ? requestedLocale : "en-US";
  const handle = decodeURIComponent(location.pathname.match(/^\/products\/([^/?#]+)/)?.[1] || new URLSearchParams(location.search).get("handle") || "custom-funko-figurine-from-photo");

  const nodes = {
    title: document.querySelector("[data-product-title]"),
    breadcrumb: document.querySelector("[data-product-breadcrumb]"),
    category: document.querySelector("[data-product-category]"),
    image: document.querySelector("[data-product-image]"),
    thumb: document.querySelector("[data-product-thumb]"),
    price: document.querySelector("[data-product-price]"),
    compare: document.querySelector("[data-product-compare]"),
    optionGroups: document.querySelector("[data-option-groups]"),
    engravingField: document.querySelector("[data-engraving-field]"),
    engravingInput: document.querySelector("[data-engraving-input]"),
    input: document.querySelector("[data-photo-input]"),
    sourcePreview: document.querySelector("[data-source-preview]"),
    selectedPreview: document.querySelector("[data-selected-preview]"),
    selectedPreviewImage: document.querySelector("[data-selected-preview] img"),
    uploadCopy: document.querySelector("[data-upload-copy]"),
    note: document.querySelector("[data-customer-note]"),
    turnstileWrap: document.querySelector("[data-turnstile-wrap]"),
    turnstileWidget: document.querySelector("[data-turnstile-widget]"),
    status: document.querySelector("[data-customizer-status]"),
    generate: document.querySelector("[data-generate-button]"),
    cartButton: document.querySelector("[data-cart-button]"),
    buyNow: document.querySelector("[data-buy-now]"),
    dialog: document.querySelector("[data-preview-dialog]"),
    previewGrid: document.querySelector("[data-preview-grid]"),
    stageImage: document.querySelector("[data-stage-image]"),
    stageLoader: document.querySelector("[data-stage-loader]"),
    stageEmpty: document.querySelector("[data-stage-empty]"),
    stageLabel: document.querySelector("[data-stage-label]"),
    stageStatus: document.querySelector("[data-stage-status]"),
    stageRetry: document.querySelector("[data-stage-retry]"),
    confirm: document.querySelector("[data-confirm-selection]"),
    historyDialog: document.querySelector("[data-history-dialog]"),
    historyList: document.querySelector("[data-history-list]"),
    generationDialog: document.querySelector("[data-generation-dialog]"),
    generationSource: document.querySelector("[data-generation-source]"),
    generationProduct: document.querySelector("[data-generation-product]"),
    generationProgress: document.querySelector("[data-generation-progress]"),
    generationPercent: document.querySelector("[data-generation-percent]"),
    generationPhase: document.querySelector("[data-generation-phase]"),
    generationTime: document.querySelector("[data-generation-time]"),
    cartQuantity: document.querySelector("[data-cart-quantity]"),
    cartVariant: document.querySelector("[data-cart-variant]"),
    cartForm: document.querySelector("[data-cart-form]"),
    cartDrawer: document.querySelector("[data-cart-drawer]"),
    cartDrawerItems: document.querySelector("[data-cart-drawer-items]"),
  };

  const state = {
    product: null,
    dictionary: null,
    file: null,
    sourceObjectUrl: "",
    sessionId: "",
    sessionToken: "",
    tasks: [],
    selectedTaskId: "",
    activeSlot: 1,
    batchKey: "",
    retryKeys: {},
    pollTimer: null,
    turnstileToken: "",
    turnstileWidgetId: "",
    securityChallenge: "",
    securityProof: null,
    securityReady: false,
    generationMode: "enhanced",
    autoStartRequested: false,
    selectedOptions: {},
    selectedPreviewUrl: "",
    lineItemProperties: {},
    generationTimer: null,
    generationStartedAt: 0,
    generationExpectedMs: 80000,
    estimatedProgress: 0,
  };

  const money = (value) => new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);

  const themeAsset = (name) => window.FORM_OF_US_THEME_ASSETS?.[name] || `/assets/${name}`;
  const stableKey = (prefix) => `${prefix}:${crypto.randomUUID()}`;
  const getNested = (object, key) => key.split(".").reduce((current, part) => current && current[part], object);
  const copy = (key, fallback) => getNested(state.dictionary, key) || fallback;
  const optionLabel = (slot) => copy(`preview.option_${slot}`, `Option ${slot}`);
  const statusLabel = (status) => copy(`preview.status_${status}`, String(status || "waiting").replace(/_/g, " "));

  function applyLocaleCopy() {
    document.documentElement.lang = locale;
    document.querySelectorAll("[data-i18n]").forEach((node) => {
      const value = getNested(state.dictionary, node.dataset.i18n);
      if (typeof value === "string") node.textContent = value;
    });
    if (nodes.note) nodes.note.placeholder = copy("product.note_placeholder", nodes.note.placeholder);
  }

  async function loadDictionary() {
    if (window.FORM_OF_US_LOCALES?.[locale]) return window.FORM_OF_US_LOCALES[locale];
    const response = await fetch(`/locales/${encodeURIComponent(locale)}.json`);
    if (!response.ok) throw new Error("locale_load_failed");
    return response.json();
  }

  function setStatus(message, error = false) {
    nodes.status.textContent = message;
    nodes.status.classList.toggle("is-error", error);
  }

  function stagedError(key, fallback, error) {
    const original = String(error?.message || "");
    const message = error instanceof TypeError || /fetch|network|load failed/i.test(original)
      ? copy(key, fallback)
      : original || copy(key, fallback);
    const staged = new Error(message);
    staged.formOfUsStage = key;
    return staged;
  }

  function updateGenerateAvailability() {
    nodes.generate.disabled = !state.file || (!isLocalDemo && !state.turnstileToken && !state.securityReady);
  }

  function proofMatches(bytes, difficulty) {
    const wholeBytes = Math.floor(difficulty / 8);
    const remainingBits = difficulty % 8;
    for (let index = 0; index < wholeBytes; index += 1) if (bytes[index] !== 0) return false;
    return !remainingBits || (bytes[wholeBytes] & (0xff << (8 - remainingBits))) === 0;
  }

  async function prepareFallbackSecurity() {
    state.securityReady = false;
    updateGenerateAvailability();
    const payload = await api("/api/shopify/customization/security-challenge");
    const encoder = new TextEncoder();
    for (let counter = 0; counter <= 100000000; counter += 1) {
      const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", encoder.encode(`${payload.challenge}:${counter}`)));
      if (proofMatches(digest, Number(payload.difficulty || 12))) {
        state.securityChallenge = payload.challenge;
        state.securityProof = counter;
        state.securityReady = true;
        nodes.turnstileWrap.hidden = true;
        updateGenerateAvailability();
        if (state.file && state.autoStartRequested) {
          state.autoStartRequested = false;
          startGeneration();
        }
        return;
      }
    }
    throw new Error("Secure preview check could not be completed.");
  }

  function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-form-of-us-turnstile="1"]');
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.formOfUsTurnstile = "1";
      script.addEventListener("load", resolve, { once: true });
      script.addEventListener("error", () => reject(new Error("Security check could not be loaded.")), { once: true });
      document.head.append(script);
    });
  }

  async function initializeTurnstile() {
    if (isLocalDemo) return;
    const config = await api("/api/shopify/customization/config");
    if (!config.turnstile_site_key) return prepareFallbackSecurity();
    try { await loadTurnstileScript(); } catch { return prepareFallbackSecurity(); }
    state.turnstileWidgetId = window.turnstile.render(nodes.turnstileWidget, {
      sitekey: config.turnstile_site_key,
      theme: "light",
      size: "invisible",
      execution: "execute",
      appearance: "interaction-only",
      callback: (token) => {
        state.turnstileToken = token;
        updateGenerateAvailability();
        if (state.file) setStatus(copy("product.generate", "Choose from four online previews when you are ready."));
        if (state.file && state.autoStartRequested) {
          state.autoStartRequested = false;
          startGeneration();
        }
      },
      "expired-callback": () => {
        state.turnstileToken = "";
        updateGenerateAvailability();
        setStatus(copy("product.error_generic", "Security check expired. Please complete it again."), true);
      },
      "error-callback": () => {
        state.turnstileToken = "";
        prepareFallbackSecurity().catch((error) => setStatus(error.message, true));
      },
    });
    updateGenerateAvailability();
    if (state.file && state.autoStartRequested) window.turnstile.execute(state.turnstileWidgetId);
  }

  function authHeaders(idempotencyKey = "") {
    const headers = { "content-type": "application/json" };
    if (state.sessionToken) headers["x-customization-session"] = state.sessionToken;
    if (idempotencyKey) headers["x-idempotency-key"] = idempotencyKey;
    return headers;
  }

  async function api(path, options = {}) {
    const response = await fetch(apiBase + path, options);
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const fallback = copy("product.error_generic", `Request failed (${response.status})`);
      throw new Error(locale === "en-US" ? payload?.error?.message || fallback : fallback);
    }
    return payload;
  }

  function saveSession() {
    if (!state.sessionId || !state.sessionToken) return;
    localStorage.setItem("formOfUsCustomization", JSON.stringify({
      sessionId: state.sessionId,
      sessionToken: state.sessionToken,
      productHandle: handle,
      createdAt: Date.now(),
    }));
  }

  const escapeHtml = (value) => String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

  function selectedOption(group) {
    const selectedId = state.selectedOptions[group.id] || group.options?.[0]?.id;
    return group.options?.find((option) => String(option.id) === String(selectedId)) || group.options?.[0] || {};
  }

  function selectedOptionList() {
    return (state.product?.option_groups || []).map((group) => ({ group, option: selectedOption(group) }));
  }

  function currentPrice() {
    const options = selectedOptionList().map(({ option }) => option);
    const pricedOption = options.find((option) => Number.isFinite(Number(option.price)));
    return Number(pricedOption?.price ?? state.product?.price ?? 0)
      + options.reduce((sum, option) => sum + Number(option.price_delta || 0), 0);
  }

  function currentVariant() {
    const product = window.FORM_OF_US_SHOPIFY_PRODUCT;
    if (!product?.variants?.length) return null;
    const labels = selectedOptionList().map(({ option }) => option.label);
    return product.variants.find((variant) => labels.every((label, index) => variant.options?.[index] === label)) || null;
  }

  function renderOptionGroups(product) {
    state.selectedOptions = Object.fromEntries((product.option_groups || []).map((group) => [group.id, String(group.options?.[0]?.id || "")]));
    if (!nodes.optionGroups) return;
    nodes.optionGroups.innerHTML = (product.option_groups || []).map((group) => `
      <fieldset class="option-fieldset option-fieldset--dynamic" data-option-group="${escapeHtml(group.id)}">
        <legend>${escapeHtml(group.label)}: <strong data-option-summary="${escapeHtml(group.id)}">${escapeHtml(group.options?.[0]?.label || "")}</strong></legend>
        <div class="option-card-grid option-card-grid--${Math.min(group.options?.length || 1, 4)}">
          ${(group.options || []).map((option, index) => `<label class="option-card">
            <input type="radio" name="option_${escapeHtml(group.id)}" value="${escapeHtml(option.id)}" data-option-group-id="${escapeHtml(group.id)}" data-option-id="${escapeHtml(option.id)}" ${index === 0 ? "checked" : ""}>
            <span><b>${escapeHtml(option.short_label || option.label)}</b>${option.detail ? `<small>${escapeHtml(option.detail)}</small>` : ""}${Number.isFinite(Number(option.price)) ? `<small>${money(Number(option.price))}</small>` : ""}</span>
          </label>`).join("")}
        </div>
      </fieldset>`).join("");
    if (nodes.engravingField) nodes.engravingField.hidden = !product.engraving_included;
  }

  function renderProduct(product) {
    state.product = product;
    const title = product.title[locale] || product.title["en-US"];
    const imageUrl = themeAsset(product.image || `product-${product.handle}-v1.png`);
    document.title = `${title} — FORM OF US`;
    nodes.title.textContent = title;
    document.querySelectorAll("[data-product-title]").forEach((node) => { node.textContent = title; });
    nodes.breadcrumb.textContent = title;
    nodes.category.textContent = product.category.replace(/_/g, " ");
    nodes.image.src = imageUrl;
    nodes.image.alt = title;
    nodes.thumb.src = imageUrl;
    const gallery = Array.isArray(product.gallery) && product.gallery.length ? product.gallery : [product.image];
    const galleryStories = [
      { title: copy("v3.gallery_main_title", "Photo to finished keepsake"), body: copy("v3.gallery_main_body", "See the source, finished piece and closed FORM OF US box together.") },
      { title: copy("v3.gallery_details_title", "Designed around the details"), body: copy("v3.gallery_details_body", "Hair, clothing, markings and colors are interpreted from your photo.") },
      { title: copy("v3.gallery_steps_title", "Scale and everyday use"), body: copy("v3.gallery_steps_body", "See the finished keepsake in a hand, on a desk or attached to a bag so its scale is easy to understand.") },
      { title: copy("v3.gallery_receive_title", "Ready to give"), body: copy("v3.gallery_receive_body", "Your approved design is packed in a closed sky-blue and lavender FORM OF US toy box.") },
    ];
    const thumbs = document.querySelector("[data-product-thumbs]");
    if (thumbs) thumbs.innerHTML = gallery.map((asset, index) => `<button class="${index === 0 ? "active" : ""}" type="button" data-gallery-image="${themeAsset(asset)}" data-gallery-alt="${title.replace(/"/g, "&quot;")} — view ${index + 1}" data-gallery-title="${galleryStories[index % galleryStories.length].title.replace(/"/g, "&quot;")}" data-gallery-body="${galleryStories[index % galleryStories.length].body.replace(/"/g, "&quot;")}" aria-label="View product image ${index + 1}"><img ${index === 0 ? "data-product-thumb" : ""} src="${themeAsset(asset)}" alt="${title.replace(/"/g, "&quot;")} — view ${index + 1}"><span>${galleryStories[index % galleryStories.length].title}</span></button>`).join("");
    nodes.price.textContent = money(product.price);
    document.querySelectorAll("[data-product-price]").forEach((node) => { node.textContent = money(product.price); });
    nodes.compare.textContent = money(product.compare_at_price);
    if (product.product_type === "premium_gift_box") {
      document.body.classList.add("is-packaging-product");
      document.querySelectorAll(".option-fieldset, .customization-box, [data-generate-button], [data-buy-now], .sticky-product-bar").forEach((node) => { node.hidden = true; });
      const lead = document.querySelector(".product-lead");
      if (lead) lead.textContent = copy("v3.packaging_product_body", "A closed, gift-ready sky-blue FORM OF US collector box. Add it to a personalized gift or order it separately.");
      if (nodes.cartButton) {
        nodes.cartButton.disabled = false;
        nodes.cartButton.textContent = copy("v3.packaging_add", "Add premium gift box to cart");
      }
      const delivery = document.querySelector(".delivery-note");
      if (delivery) delivery.textContent = copy("v3.packaging_delivery", "Ships as a separate gift-ready packaging item.");
      return;
    }
    renderOptionGroups(product);
    updateDisplayedPrice();

    const featureAssets = {
      wide: gallery[1] || gallery[0],
      before: gallery[0],
      after: gallery[2] || gallery[0],
      compare: gallery[3] || gallery[0],
    };
    const featureWide = document.querySelector("[data-feature-wide]");
    const featureBefore = document.querySelector("[data-feature-before]");
    const featureAfter = document.querySelector("[data-feature-after]");
    const compareOurs = document.querySelector("[data-compare-ours]");
    if (featureWide) featureWide.src = themeAsset(featureAssets.wide);
    if (featureBefore) featureBefore.src = themeAsset(featureAssets.before);
    if (featureAfter) featureAfter.src = themeAsset(featureAssets.after);
    if (compareOurs) compareOurs.src = themeAsset(featureAssets.compare);
    const featureCopyKeys = {
      pet_keychain: ["pet_look_title", "pet_look_body"],
      pet_figurine: ["pet_look_title", "pet_look_body"],
      crystal_globe: ["crystal_look_title", "crystal_look_body"],
      building_block_figurine: ["block_look_title", "block_look_body"],
      couple_figurine: ["couple_look_title", "couple_look_body"],
      funko_figurine: ["look_title", "look_body"],
    };
    const [featureTitleKey, featureBodyKey] = featureCopyKeys[product.product_type] || featureCopyKeys.funko_figurine;
    const featureTitle = document.querySelector("[data-feature-title]");
    const featureBody = document.querySelector("[data-feature-body]");
    if (featureTitle) featureTitle.textContent = copy(`v3.${featureTitleKey}`, featureTitle.textContent);
    if (featureBody) featureBody.textContent = copy(`v3.${featureBodyKey}`, featureBody.textContent);
    const proof = document.querySelector("[data-proof-carousel]");
    if (proof) proof.innerHTML = gallery.map((asset, index) => `<figure><img loading="lazy" src="${themeAsset(asset)}" alt="${title.replace(/"/g, "&quot;")} — product example ${index + 1}"><figcaption>${galleryStories[index % galleryStories.length].title}</figcaption></figure>`).join("");
  }

  function currentStyle() {
    return state.product?.product_type || "custom";
  }

  function currentConfiguration() {
    const selected = selectedOptionList();
    const primary = selected.find(({ group }) => ["quantity", "configuration", "base"].includes(group.id))?.option || selected[0]?.option || {};
    return {
      label: primary.label || "Custom item",
      quantity: 1,
      price: currentPrice(),
      people: Number(primary.people ?? state.product?.people ?? 0),
      pets: Number(primary.pets ?? state.product?.pets ?? 0),
    };
  }

  function selectedSizeLabel() {
    const size = (state.product?.option_groups || []).find((group) => group.id === "size");
    return size ? selectedOption(size).label : "";
  }

  function selectedProperties() {
    const properties = Object.fromEntries(selectedOptionList().map(({ group, option }) => [group.label, option.label]));
    const engraving = nodes.engravingInput?.value.trim();
    if (engraving) properties.Engraving = engraving;
    return properties;
  }

  function updateDisplayedPrice() {
    if (!state.product) return;
    const variant = currentVariant();
    const total = variant ? Number(variant.price || 0) / 100 : currentPrice();
    document.querySelectorAll("[data-product-price]").forEach((node) => { node.textContent = money(total); });
    if (nodes.cartQuantity) nodes.cartQuantity.value = "1";
    if (nodes.cartVariant && variant) nodes.cartVariant.value = String(variant.id);
  }

  function updateSlot(slotNumber, task = {}) {
    const card = nodes.previewGrid.querySelector(`[data-slot="${slotNumber}"]`);
    if (!card) return;
    const image = card.querySelector("img");
    const status = card.querySelector(".preview-slot__meta span");
    const taskStatus = task.status || "waiting";
    card.dataset.taskId = task.task_id || "";
    card.classList.toggle("is-ready", taskStatus === "completed");
    card.classList.toggle("is-failed", taskStatus === "failed");
    card.classList.toggle("selected", Boolean(task.selected) || task.task_id === state.selectedTaskId);
    status.textContent = statusLabel(taskStatus);
    if (task.preview_url) {
      image.src = task.preview_url;
      image.hidden = false;
    } else if (isLocalDemo && ["completed", "failed"].includes(taskStatus)) {
      image.src = themeAsset(state.product.gallery?.[slotNumber - 1] || state.product.image || `product-${handle}-v1.png`);
      image.hidden = taskStatus !== "completed";
    } else {
      image.hidden = true;
    }
  }

  function chooseActiveTask(tasks, activeSlot) {
    const currentActive = tasks.find((task) => Number(task.slot) === Number(activeSlot));
    if (currentActive?.status === "completed") return currentActive;
    return tasks.find((task) => task.status === "completed")
      || currentActive
      || tasks[0]
      || { slot: 1, status: "waiting" };
  }

  function isTerminalTaskSet(tasks) {
    return tasks.length === 4 && tasks.every((task) => ["completed", "failed"].includes(task.status));
  }

  function renderTasks(tasks) {
    state.tasks = [...tasks].sort((a, b) => Number(a.slot) - Number(b.slot));
    for (let slot = 1; slot <= 4; slot += 1) {
      updateSlot(slot, state.tasks.find((task) => Number(task.slot) === slot) || { slot, status: "waiting" });
    }
    const active = chooseActiveTask(state.tasks, state.activeSlot);
    state.activeSlot = Number(active.slot) || 1;
    const activeStatus = active.status || "waiting";
    nodes.stageLabel.textContent = optionLabel(state.activeSlot);
    nodes.stageStatus.textContent = statusLabel(activeStatus);
    nodes.stageRetry.hidden = activeStatus !== "failed";
    nodes.stageRetry.dataset.stageRetry = String(state.activeSlot);
    nodes.stageLoader.hidden = !["waiting", "queued", "processing"].includes(activeStatus);
    if (active.preview_url || (isLocalDemo && activeStatus === "completed")) {
      nodes.stageImage.src = active.preview_url || themeAsset(state.product.image || `product-${handle}-v1.png`);
      nodes.stageImage.hidden = false;
      nodes.stageEmpty.hidden = true;
    } else {
      nodes.stageImage.hidden = true;
      nodes.stageEmpty.hidden = false;
      nodes.stageEmpty.textContent = activeStatus === "failed"
        ? copy("product.retry_one", "This option needs another try.")
        : copy("product.preparing", "Four independent previews are being prepared.");
    }
    const selected = state.tasks.find((task) => task.selected || task.task_id === state.selectedTaskId);
    nodes.confirm.disabled = !selected;
    updateGenerationProgress(state.tasks);
    const terminal = isTerminalTaskSet(state.tasks);
    if (terminal && nodes.generationDialog?.open) {
      closeGenerationDialog();
      if (!nodes.dialog.open) nodes.dialog.showModal();
    }
  }

  function setGenerationProgress(value) {
    const progress = Math.max(0, Math.min(100, Math.round(value || 0)));
    state.estimatedProgress = progress;
    if (nodes.generationProgress) {
      nodes.generationProgress.setAttribute("aria-valuenow", String(progress));
      nodes.generationProgress.querySelector("span").style.width = `${progress}%`;
    }
    if (nodes.generationPercent) nodes.generationPercent.textContent = `${progress}%`;
    if (nodes.generationPhase) {
      nodes.generationPhase.textContent = progress < 18
        ? copy("product.generating_prepare", "Preparing your photo…")
        : progress < 48
          ? copy("product.generating_create", "Creating four independent options…")
          : progress < 78
            ? copy("product.generating_refine", "Refining faces, details and product shape…")
            : copy("product.generating_finish", "Finishing your preview set…");
    }
  }

  function updateGenerationProgress(tasks = []) {
    if (!nodes.generationDialog?.open) return;
    const actual = tasks.length
      ? tasks.reduce((sum, task) => sum + Number(task.progress || (task.status === "completed" ? 100 : 0)), 0) / tasks.length
      : 0;
    setGenerationProgress(Math.max(state.estimatedProgress, actual));
  }

  function openGenerationDialog() {
    if (!nodes.generationDialog) return;
    nodes.generationSource.src = state.sourceObjectUrl;
    nodes.generationProduct.src = themeAsset(state.product.gallery?.[0] || state.product.image);
    state.generationExpectedMs = state.generationMode === "quick" ? 60000 : 80000;
    state.generationStartedAt = Date.now();
    if (nodes.generationTime) nodes.generationTime.textContent = state.generationMode === "quick" ? "About 1 minute" : "About 1 min 20 sec";
    setGenerationProgress(4);
    clearInterval(state.generationTimer);
    state.generationTimer = setInterval(() => {
      if (!nodes.generationDialog.open || state.estimatedProgress >= 92) return;
      const elapsed = Date.now() - state.generationStartedAt;
      const timeProgress = 4 + (elapsed / state.generationExpectedMs) * 88;
      setGenerationProgress(Math.max(state.estimatedProgress, timeProgress));
    }, 300);
    if (!nodes.generationDialog.open) nodes.generationDialog.showModal();
  }

  function closeGenerationDialog() {
    clearInterval(state.generationTimer);
    state.generationTimer = null;
    setGenerationProgress(100);
    if (nodes.generationDialog?.open) nodes.generationDialog.close();
  }

  function demoStart() {
    state.sessionId = `demo_${Date.now()}`;
    state.sessionToken = "local-demo";
    state.tasks = [1, 2, 3, 4].map((slot) => ({ task_id: `demo_task_${slot}`, slot, status: "processing", progress: 30 }));
    saveSession();
    renderTasks(state.tasks);
    setTimeout(() => {
      state.tasks = state.tasks.map((task) => ({ ...task, status: "completed", progress: 100, preview_url: themeAsset(state.product.gallery?.[task.slot - 1] || state.product.image || `product-${handle}-v1.png`) }));
      renderTasks(state.tasks);
      setStatus(copy("preview.title", "Four local preview slots are ready."));
    }, 900);
  }

  async function createAndUploadSession() {
    const product = state.product;
    let session;
    try {
      session = await api("/api/shopify/customization/sessions", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          shop: location.host,
          catalog_id: product.id,
          product_handle: product.handle,
          product_type: product.product_type,
          locale,
          content_type: state.file.type,
          file_size: state.file.size,
          turnstile_token: state.turnstileToken,
          security_challenge: state.securityChallenge,
          security_proof: state.securityProof,
        }),
      });
    } catch (error) {
      throw stagedError("v3.connection_session", "We could not create a secure preview session. Your photo was not uploaded. Please retry.", error);
    }
    state.sessionId = session.session_id;
    state.sessionToken = session.session_token;
    try {
      await fetch(session.upload_url, {
        method: "PUT",
        headers: { "content-type": state.file.type },
        body: state.file,
      }).then((response) => {
        if (!response.ok) throw new TypeError(`Private upload failed (${response.status}).`);
      });
      await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/uploaded`, {
        method: "POST",
        headers: authHeaders(),
        body: "{}",
      });
    } catch (error) {
      state.sessionId = "";
      state.sessionToken = "";
      throw stagedError("v3.connection_upload", "The private photo upload could not finish. Your photo was not sent to generation. Please retry.", error);
    }
    saveSession();
  }

  async function startGeneration() {
    if (!state.file || !state.product) return;
    nodes.generate.disabled = true;
    nodes.generate.textContent = `${copy("product.generate", "Choose from four online previews")}…`;
    state.selectedTaskId = "";
    state.selectedPreviewUrl = "";
    state.lineItemProperties = {};
    openGenerationDialog();
    renderTasks([1, 2, 3, 4].map((slot) => ({ slot, status: "queued" })));
    try {
      if (isLocalDemo) {
        demoStart();
        return;
      }
      if (!state.sessionId) await createAndUploadSession();
      state.batchKey ||= stableKey(`batch:${state.sessionId}`);
      let payload;
      try {
        payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/generations`, {
          method: "POST",
          headers: authHeaders(state.batchKey),
          body: JSON.stringify({
            model: "gpt-image-2-2K",
            style_id: currentStyle(),
            options: {
              customer_note: nodes.note?.value.trim() || "",
              size: selectedSizeLabel(),
              configuration: currentConfiguration().label,
              people_count: currentConfiguration().people,
              pet_count: currentConfiguration().pets,
              selected_options: selectedProperties(),
              engraving: nodes.engravingInput?.value.trim() || "",
              generation_mode: state.generationMode,
            },
          }),
        });
      } catch (error) {
        throw stagedError("v3.connection_generation", "The four-preview request could not start. Your existing upload is preserved; retry when the connection is stable.", error);
      }
      renderTasks(payload.tasks || []);
      beginPolling();
    } catch (error) {
      closeGenerationDialog();
      if (nodes.dialog?.open) nodes.dialog.close();
      const message = !error.formOfUsStage && error instanceof TypeError && /fetch/i.test(error.message)
        ? copy("v3.connection_error", "The preview service could not be reached from this page. Open the published storefront and try again, or contact support if the published site also fails.")
        : error.message;
      setStatus(message, true);
      nodes.generate.disabled = false;
      nodes.generate.textContent = copy("product.generate", "Choose from four online previews");
    }
  }

  async function refreshSession() {
    let payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}`, {
      headers: authHeaders(),
    });
    const pending = (payload.tasks || []).filter((task) => !["completed", "failed"].includes(task.status));
    if (pending.length) {
      await Promise.allSettled(pending.map((task) => api(
        `/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/tasks/${encodeURIComponent(task.task_id)}`,
        { headers: authHeaders() },
      )));
      payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}`, {
        headers: authHeaders(),
      });
    }
    renderTasks(payload.tasks || []);
    const terminal = isTerminalTaskSet(payload.tasks || []);
    if (terminal) {
      clearInterval(state.pollTimer);
      state.pollTimer = null;
      nodes.generate.disabled = false;
      nodes.generate.textContent = copy("product.generate", "Generate four new previews");
      setStatus(payload.status === "partial_failed" ? copy("product.retry_one", "Some previews need another try.") : copy("preview.title", "Four previews are ready."));
    }
  }

  function beginPolling() {
    clearInterval(state.pollTimer);
    refreshSession().catch(() => {});
    state.pollTimer = setInterval(() => refreshSession().catch((error) => setStatus(error.message, true)), 2500);
  }

  async function retrySlot(slot) {
    const task = state.tasks.find((candidate) => Number(candidate.slot) === Number(slot));
    if (!task) return;
    if (isLocalDemo) {
      const updated = state.tasks.map((candidate) => Number(candidate.slot) === Number(slot) ? { ...candidate, status: "completed" } : candidate);
      renderTasks(updated);
      return;
    }
    state.retryKeys[task.task_id] ||= stableKey(`retry:${task.task_id}`);
    const payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/tasks/${encodeURIComponent(task.task_id)}/retry`, {
      method: "POST",
      headers: authHeaders(state.retryKeys[task.task_id]),
      body: "{}",
    });
    renderTasks(state.tasks.map((candidate) => candidate.task_id === task.task_id ? payload.task || payload : candidate));
    beginPolling();
  }

  async function selectSlot(slot) {
    const task = state.tasks.find((candidate) => Number(candidate.slot) === Number(slot));
    if (!task || task.status !== "completed") return;
    state.activeSlot = Number(slot);
    state.selectedTaskId = task.task_id;
    state.selectedPreviewUrl = task.preview_url || themeAsset(state.product.gallery?.[Number(slot) - 1] || state.product.image);
    showSelectedPreview(state.selectedPreviewUrl);
    renderTasks(state.tasks.map((candidate) => ({ ...candidate, selected: candidate.task_id === task.task_id })));
    if (!isLocalDemo) {
      await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/select`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ task_id: task.task_id }),
      });
    }
  }

  function showSelectedPreview(url) {
    if (!url || !nodes.selectedPreviewImage || !nodes.selectedPreview) return;
    nodes.selectedPreviewImage.src = url;
    nodes.selectedPreview.hidden = false;
  }

  async function confirmSelection() {
    if (!state.selectedTaskId) return;
    try {
      const selectedTask = state.tasks.find((task) => task.task_id === state.selectedTaskId);
      state.selectedPreviewUrl = selectedTask?.preview_url || state.selectedPreviewUrl;
      const properties = {
        _customization_session_id: state.sessionId,
        _customization_task_id: state.selectedTaskId,
        _customization_confirmed: "true",
        _customization_locale: locale,
        _customization_catalog_id: state.product.id,
        _selected_preview_url: state.selectedPreviewUrl,
        ...selectedProperties(),
      };
      state.lineItemProperties = properties;
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        Object.entries(properties).forEach(([name, value]) => {
          let field = form.querySelector(`input[name="properties[${CSS.escape(name)}]"]`);
          if (!field) {
            field = document.createElement("input");
            field.type = "hidden";
            field.name = `properties[${name}]`;
            form.append(field);
          }
          field.value = value;
        });
      }
      showSelectedPreview(state.selectedPreviewUrl);
      nodes.dialog.close();
      setStatus(copy("product.cart", "Preview selected. Your customization is ready to add to cart."));
      nodes.generate.textContent = copy("preview.confirm", "Selected · Ready for cart");
      if (nodes.cartButton) nodes.cartButton.disabled = false;
      if (nodes.buyNow) nodes.buyNow.disabled = false;
    } catch (error) {
      setStatus(error.message, true);
    }
  }

  function showHistory() {
    const saved = JSON.parse(localStorage.getItem("formOfUsCustomization") || "null");
    nodes.historyList.innerHTML = saved
      ? `<article class="history-item"><div><strong>${saved.productHandle}</strong><br><small>${new Date(saved.createdAt).toLocaleString()}</small></div><button type="button" data-restore-session>Restore</button></article>`
      : `<p>${copy("product.history_empty", "No saved session on this device yet.")}</p>`;
    nodes.historyDialog.showModal();
  }

  async function restoreHistory() {
    const saved = JSON.parse(localStorage.getItem("formOfUsCustomization") || "null");
    if (!saved || saved.productHandle !== handle) return;
    state.sessionId = saved.sessionId;
    state.sessionToken = saved.sessionToken;
    if (isLocalDemo) {
      demoStart();
      nodes.historyDialog.close();
      nodes.dialog.showModal();
      return;
    }
    await refreshSession();
    nodes.historyDialog.close();
    nodes.dialog.showModal();
    beginPolling();
  }

  function openCartDrawer() {
    if (!nodes.cartDrawer) return;
    nodes.cartDrawer.classList.add("is-open");
    nodes.cartDrawer.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("cart-drawer-open");
  }

  function closeCartDrawer() {
    if (!nodes.cartDrawer) return;
    nodes.cartDrawer.classList.remove("is-open");
    nodes.cartDrawer.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("cart-drawer-open");
  }

  function renderCartDrawer(cart) {
    if (!nodes.cartDrawerItems) return;
    const items = cart.items || [];
    const selectedSize = selectedSizeLabel();
    nodes.cartDrawer.querySelector("[data-cart-count]").textContent = String(cart.item_count || items.reduce((sum, item) => sum + Number(item.quantity || 0), 0));
    nodes.cartDrawerItems.innerHTML = items.map((item, index) => {
      const properties = Object.entries(item.properties || {}).filter(([key, value]) => value && !key.startsWith("_"));
      const isCurrentCustomization = item.properties?._customization_task_id === state.selectedTaskId;
      const image = isCurrentCustomization && state.selectedPreviewUrl
        ? state.selectedPreviewUrl
        : item.featured_image?.url || item.image || themeAsset(state.product.image);
      const detail = properties.length
        ? properties.map(([key, value]) => `${key}: ${value}`).join(" · ")
        : `${currentConfiguration().label} · ${selectedSize}`;
      const title = item.product_title || state.product.title[locale] || state.product.title["en-US"];
      const quantity = Number(item.quantity || 1);
      return `<article class="cart-drawer-item" data-cart-quantity="${quantity}"><img src="${escapeHtml(image)}" alt="${escapeHtml(title)}"><div><strong>${escapeHtml(title)}</strong><small>${escapeHtml(detail)}</small><div class="cart-drawer-item__quantity"><button type="button" aria-label="Decrease quantity" data-cart-line="${index + 1}" data-cart-delta="-1">−</button><span>${quantity}</span><button type="button" aria-label="Increase quantity" data-cart-line="${index + 1}" data-cart-delta="1">＋</button><button class="cart-drawer-item__remove" type="button" data-cart-line="${index + 1}" data-cart-remove>Remove</button></div></div><b>${money(Number(item.final_line_price || 0) / 100)}</b></article>`;
    }).join("") || `<p class="cart-drawer__empty">Your cart is empty.</p>`;
    const subtotalValue = Number(cart.total_price || 0) / 100;
    const subtotal = money(subtotalValue);
    const shippingValue = items.length ? 7.99 : 0;
    const estimatedTotal = money(subtotalValue + shippingValue);
    nodes.cartDrawer.querySelector("[data-cart-subtotal]").textContent = subtotal;
    nodes.cartDrawer.querySelector("[data-cart-checkout-total]").textContent = estimatedTotal;
    nodes.cartDrawer.querySelector("[data-cart-checkout-button-total]").textContent = estimatedTotal;
    const shippingCopy = nodes.cartDrawer.querySelector("[data-cart-shipping-copy]");
    if (shippingCopy) shippingCopy.textContent = "Flat standard shipping · $7.99";
    const shippingBar = nodes.cartDrawer.querySelector("[data-cart-shipping-bar]");
    if (shippingBar) shippingBar.style.width = items.length ? "100%" : "0%";
    const note = nodes.cartDrawer.querySelector("[data-cart-note]");
    if (note && document.activeElement !== note) note.value = cart.note || "";
  }

  async function loadCart() {
    const response = await fetch("/cart.js", { headers: { accept: "application/json" } });
    if (!response.ok) throw new Error("Cart could not be loaded.");
    return response.json();
  }

  async function changeCartLine(line, quantity) {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ line, quantity }),
    });
    if (!response.ok) throw new Error("Cart could not be updated.");
    const cart = await response.json();
    renderCartDrawer(cart);
  }

  async function saveCartNote() {
    const note = nodes.cartDrawer?.querySelector("[data-cart-note]")?.value.trim() || "";
    const status = nodes.cartDrawer?.querySelector("[data-cart-note-status]");
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ note }),
    });
    if (!response.ok) throw new Error("The order note could not be saved.");
    renderCartDrawer(await response.json());
    if (status) status.textContent = "Saved";
  }

  async function addToCart(event, { openDrawer = true } = {}) {
    event?.preventDefault?.();
    const isPackaging = state.product?.product_type === "premium_gift_box";
    if ((!state.selectedTaskId && !isPackaging) || !nodes.cartForm) return false;
    try {
      let cart;
      if (isLocalDemo) {
        const price = currentPrice();
        cart = {
          item_count: 1,
          total_price: price * 100,
          items: [{
            product_title: state.product.title[locale] || state.product.title["en-US"],
            quantity: 1,
            final_line_price: price * 100,
            image: state.selectedPreviewUrl || themeAsset(state.product.image),
            properties: { ...selectedProperties(), _customization_task_id: state.selectedTaskId },
          }],
        };
      } else {
        if (!isPackaging) {
          const payload = await api(`/api/shopify/customization/sessions/${encodeURIComponent(state.sessionId)}/added-to-cart`, {
            method: "POST",
            headers: authHeaders(),
            body: "{}",
          });
          const form = nodes.cartForm;
          state.lineItemProperties = { ...state.lineItemProperties, ...payload.line_item_properties, ...selectedProperties() };
          Object.entries(state.lineItemProperties).forEach(([name, value]) => {
            let field = form.querySelector(`input[name="properties[${CSS.escape(name)}]"]`);
            if (!field) {
              field = document.createElement("input");
              field.type = "hidden";
              field.name = `properties[${name}]`;
              form.append(field);
            }
            field.value = value;
          });
        }
        const response = await fetch(nodes.cartForm.action, {
          method: "POST",
          headers: { accept: "application/json" },
          body: new FormData(nodes.cartForm),
        });
        if (!response.ok) throw new Error("This option could not be added to the cart.");
        cart = await loadCart();
      }
      renderCartDrawer(cart);
      if (openDrawer) openCartDrawer();
      return true;
    } catch (error) {
      setStatus(error.message, true);
      return false;
    }
  }

  function acceptConfirmedPhoto(file, mode = "enhanced") {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 10 * 1024 * 1024) {
      nodes.input.value = "";
      setStatus(copy("product.upload_help", "Please choose a JPG, PNG or WEBP image under 10 MB."), true);
      return;
    }
    if (state.sourceObjectUrl) URL.revokeObjectURL(state.sourceObjectUrl);
    state.file = file;
    state.selectedTaskId = "";
    state.selectedPreviewUrl = "";
    state.sessionId = "";
    state.sessionToken = "";
    state.batchKey = "";
    state.tasks = [];
    state.generationMode = mode === "quick" ? "quick" : "enhanced";
    state.sourceObjectUrl = URL.createObjectURL(file);
    nodes.sourcePreview.src = state.sourceObjectUrl;
    nodes.sourcePreview.hidden = false;
    nodes.uploadCopy.hidden = true;
    if (nodes.selectedPreview) nodes.selectedPreview.hidden = true;
    if (nodes.selectedPreviewImage) nodes.selectedPreviewImage.removeAttribute("src");
    if (nodes.cartButton) nodes.cartButton.disabled = true;
    if (nodes.buyNow) nodes.buyNow.disabled = true;
    updateGenerateAvailability();
    setStatus(isLocalDemo || state.turnstileToken || state.securityReady
      ? copy("product.generate", "Photo confirmed. Choose your previews when you are ready.")
      : copy("product.security", "Photo confirmed. Preparing a secure preview session."));
    if (isLocalDemo || state.turnstileToken || state.securityReady) {
      startGeneration();
    } else {
      state.autoStartRequested = true;
      if (window.turnstile && state.turnstileWidgetId !== "") {
        try { window.turnstile.execute(state.turnstileWidgetId); }
        catch { prepareFallbackSecurity().catch((error) => setStatus(error.message, true)); }
      }
    }
  }

  function bindEvents() {
    nodes.input.addEventListener("formofus:photo-confirmed", (event) => acceptConfirmedPhoto(event.detail?.file, event.detail?.mode));
    if (!document.querySelector("[data-upload-review-dialog]")) {
      nodes.input.addEventListener("change", () => acceptConfirmedPhoto(nodes.input.files?.[0]));
    }
    if (window.FORM_OF_US_CONFIRMED_PHOTO) {
      acceptConfirmedPhoto(window.FORM_OF_US_CONFIRMED_PHOTO);
      window.FORM_OF_US_CONFIRMED_PHOTO = null;
    }
    document.addEventListener("change", (event) => {
      const input = event.target.closest?.("[data-option-group-id]");
      if (!input) return;
      state.selectedOptions[input.dataset.optionGroupId] = input.dataset.optionId;
      const group = state.product.option_groups.find((candidate) => candidate.id === input.dataset.optionGroupId);
      const option = group && selectedOption(group);
      const summary = nodes.optionGroups?.querySelector(`[data-option-summary="${CSS.escape(input.dataset.optionGroupId)}"]`);
      if (summary && option) summary.textContent = option.label;
      updateDisplayedPrice();
    });
    nodes.generate.addEventListener("click", startGeneration);
    nodes.previewGrid.addEventListener("click", (event) => {
      const retry = event.target.closest("[data-retry-slot]");
      const select = event.target.closest("[data-select-slot]");
      const card = event.target.closest("[data-slot]");
      if (retry) retrySlot(retry.dataset.retrySlot).catch((error) => setStatus(error.message, true));
      if (select) selectSlot(select.dataset.selectSlot).catch((error) => setStatus(error.message, true));
      if (card && !retry && !select) selectSlot(card.dataset.slot).catch((error) => setStatus(error.message, true));
    });
    nodes.previewGrid.addEventListener("keydown", (event) => {
      const card = event.target.closest("[data-slot]");
      if (card && ["Enter", " "].includes(event.key)) {
        event.preventDefault();
        selectSlot(card.dataset.slot).catch((error) => setStatus(error.message, true));
      }
    });
    nodes.stageRetry.addEventListener("click", () => {
      retrySlot(nodes.stageRetry.dataset.stageRetry).catch((error) => setStatus(error.message, true));
    });
    nodes.confirm.addEventListener("click", confirmSelection);
    document.querySelector("[data-generation-close]")?.addEventListener("click", closeGenerationDialog);
    document.querySelector("[data-preview-close]").addEventListener("click", () => nodes.dialog.close());
    document.querySelector("[data-generate-again]").addEventListener("click", () => {
      state.sessionId = "";
      state.sessionToken = "";
      state.batchKey = "";
      state.tasks = [];
      nodes.dialog.close();
      if (isLocalDemo) {
        startGeneration();
        return;
      }
      state.turnstileToken = "";
      state.securityChallenge = "";
      state.securityProof = null;
      state.securityReady = false;
      state.autoStartRequested = true;
      if (window.turnstile && state.turnstileWidgetId !== "") {
        window.turnstile.reset(state.turnstileWidgetId);
        try { window.turnstile.execute(state.turnstileWidgetId); }
        catch { prepareFallbackSecurity().catch((error) => setStatus(error.message, true)); }
      } else prepareFallbackSecurity().catch((error) => setStatus(error.message, true));
      updateGenerateAvailability();
    });
    document.querySelector("[data-history-button]").addEventListener("click", showHistory);
    document.querySelector("[data-history-close]").addEventListener("click", () => nodes.historyDialog.close());
    nodes.historyList.addEventListener("click", (event) => {
      if (event.target.closest("[data-restore-session]")) restoreHistory().catch((error) => setStatus(error.message, true));
    });
    nodes.cartForm?.addEventListener("submit", addToCart);
    nodes.buyNow?.addEventListener("click", async () => {
      const added = await addToCart(new Event("submit"), { openDrawer: false });
      if (added) window.location.assign("/checkout");
    });
    nodes.cartDrawer?.querySelectorAll("[data-cart-drawer-close]").forEach((button) => button.addEventListener("click", closeCartDrawer));
    nodes.cartDrawerItems?.addEventListener("click", (event) => {
      const button = event.target.closest("[data-cart-line]");
      if (!button) return;
      const line = Number(button.dataset.cartLine);
      const item = button.closest("[data-cart-quantity]");
      const currentQuantity = Number(item?.dataset.cartQuantity || 1);
      const quantity = button.hasAttribute("data-cart-remove")
        ? 0
        : Math.max(0, currentQuantity + Number(button.dataset.cartDelta || 0));
      button.disabled = true;
      changeCartLine(line, quantity).catch((error) => setStatus(error.message, true));
    });
    nodes.cartDrawer?.querySelector("[data-cart-note-save]")?.addEventListener("click", (event) => {
      event.currentTarget.disabled = true;
      saveCartNote()
        .catch((error) => setStatus(error.message, true))
        .finally(() => { event.currentTarget.disabled = false; });
    });
    nodes.cartDrawer?.querySelector("[data-cart-discount-form]")?.addEventListener("submit", (event) => {
      event.preventDefault();
      const code = new FormData(event.currentTarget).get("discount")?.toString().trim();
      if (code) window.location.assign(`/discount/${encodeURIComponent(code)}?redirect=/checkout`);
    });
  }

  async function initialize() {
    state.dictionary = await loadDictionary();
    applyLocaleCopy();
    let catalog = window.FORM_OF_US_CATALOG;
    if (!catalog) {
      const response = await fetch("/catalog/products.json");
      catalog = await response.json();
    }
    const product = catalog.products.find((candidate) => candidate.handle === handle) || catalog.products[0];
    renderProduct(product);
    const localeSelect = document.querySelector("[data-locale-select]");
    localeSelect.value = locale;
    localeSelect.addEventListener("change", () => {
      localStorage.setItem("formOfUsLocale", localeSelect.value);
      location.reload();
    });
    if (product.product_type === "premium_gift_box") return;
    bindEvents();
    updateGenerateAvailability();
    initializeTurnstile().catch((error) => {
      const message = stagedError("v3.connection_security", "The secure preview check is being prepared automatically. Please wait a moment and retry.", error).message;
      setStatus(message, true);
      updateGenerateAvailability();
    });
  }

  initialize().catch((error) => setStatus(error.message, true));
})();
